import { useEffect, useState } from 'react';
import { listWebhookLogs } from '@/services/webhookLogs';
import type { WebhookLog } from '@/domain/types';

export default function AdminWebhooks() {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listWebhookLogs()
      .then((data) => { if (alive) setLogs(data); })
      .catch(() => { if (alive) setLogs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = filter === 'all' ? logs : logs.filter((w) => w.status === filter);

  const formatPayload = (payload: string) => {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">Webhook Logs</h1>
        <p className="text-sm text-surface-500 mt-1">Review incoming payment webhook events for debugging.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'success', 'failed'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filter === f ? 'bg-brand-600 text-white' : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-surface-400 text-xl" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-bug-line text-3xl text-surface-300 mb-3" />
              <p className="text-sm font-medium text-surface-500">No webhook events yet</p>
              <p className="text-xs text-surface-400 mt-1">Webhook events will appear here after payment gateways send callbacks.</p>
            </div>
          ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Event</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Order Ref</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Timestamp</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Payload</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((log) => (
                <tr key={log.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                  <td className="px-5 py-3 font-semibold text-surface-900">{log.eventType}</td>
                  <td className="px-5 py-3 text-surface-600">{log.orderRef}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${log.status === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {log.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-surface-500 text-xs">{new Date(log.createdAt).toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >
                      View Payload
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {/* Payload Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setSelectedLog(null)}
          />
          <div className="relative bg-white rounded-xl border border-surface-200 shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200">
              <div>
                <h3 className="font-semibold text-surface-900 text-sm">Webhook Payload</h3>
                <p className="text-xs text-surface-500 mt-0.5">
                  {selectedLog.eventType} — {selectedLog.orderRef}
                </p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-auto">
              <div className="bg-surface-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs font-mono text-green-400 leading-relaxed whitespace-pre-wrap">
                  {formatPayload(selectedLog.payload)}
                </pre>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="bg-surface-50 rounded-lg p-3">
                  <p className="text-surface-500">Provider</p>
                  <p className="font-medium text-surface-900 mt-0.5 capitalize">{selectedLog.provider}</p>
                </div>
                <div className="bg-surface-50 rounded-lg p-3">
                  <p className="text-surface-500">Status</p>
                  <p className="font-medium text-surface-900 mt-0.5 capitalize">{selectedLog.status}</p>
                </div>
                <div className="bg-surface-50 rounded-lg p-3">
                  <p className="text-surface-500">Event Type</p>
                  <p className="font-medium text-surface-900 mt-0.5">{selectedLog.eventType}</p>
                </div>
                <div className="bg-surface-50 rounded-lg p-3">
                  <p className="text-surface-500">Received At</p>
                  <p className="font-medium text-surface-900 mt-0.5">{new Date(selectedLog.createdAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-200 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}